'use client'

import Image, { ImageProps } from "next/image"
import { useState } from "react"
import { ImageIcon } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"

// ✅ Extend from Next.js ImageProps but make some optional overrides
interface ImageWithFallbackProps extends Omit<ImageProps, "src" | "alt"> {
    src: string
    alt: string
    fallbackClassName?: string
}

export const ImageWithFallback = ({
    src,
    alt,
    className = "",
    fallbackClassName = "",
    ...props
}: ImageWithFallbackProps) => {
    const [hasError, setHasError] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    if (!src || hasError) {
        return (
            <div
                className={`h-full flex flex-col items-center justify-center text-gray-400 ${fallbackClassName}`}
            >
                <ImageIcon className="w-16 h-16 mb-2" />
                <p className="text-sm">Image failed to load</p>
            </div>
        )
    }

    return (
        <div className="relative w-full h-full">
            {isLoading && (
                <div className="absolute inset-0">
                    <Skeleton className={`w-full h-full ${fallbackClassName}`} />
                </div>
            )}

            <Image
                src={src}
                alt={alt}
                className={`${className} ${isLoading ? "opacity-0" : "opacity-100"
                    } transition-opacity duration-500`}
                onLoadStart={() => setIsLoading(true)}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                    setHasError(true)
                    setIsLoading(false)
                }}
                {...props}
            />
        </div>
    )
}
